import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Autocomplete, { highlightSubstring } from '../Autocomplete';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import Chip from '@material-ui/core/Chip';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Avatar from '@material-ui/core/Avatar';
import gravatarUrl from '@afosto/utils/gravatarUrl';
import getContactDisplay from '@afosto/utils/getContactDisplay';
import { index as searchApi } from '@afosto/content/api/Index';
import { contacts as contactsApi } from '@afosto/contacts/api/Contacts';
import { useFormContext } from '../Form';
import translations from './translations';

const ContactSearch = ({ inputProps, name, onChange, validateContact, enableCreate, ...props }) => {
  const intl = useIntl();
  const [isFetchingContacts, setIsFetchingContacts] = useState(false);
  const [contactSuggestions, setContactSuggestions] = useState([]);
  const formContext = useFormContext();
  const { setValue } = formContext ?? {};

  /**
   * Search for contacts
   * @param {string} searchValue
   * @returns {Promise}
   */
  const searchContacts = useCallback(async searchValue => {
    try {
      setIsFetchingContacts(true);

      const response = await searchApi.searchModel('default', 'rel', 'contacts', {
        q: searchValue?.length ? [searchValue] : null,
      });
      const { data = {} } = response;
      const { hits = [] } = data;
      const suggestions = hits.reduce((acc, hit) => {
        const { metadata = {}, filters = {}, reference } = hit;

        return [
          ...acc,
          {
            ...filters,
            ...metadata,
            label: getContactDisplay(metadata),
            value: reference,
            id: reference,
          },
        ];
      }, []);
      setContactSuggestions(suggestions);
    } catch (err) {
      setContactSuggestions([]);
    } finally {
      setIsFetchingContacts(false);
    }
  }, []);

  useEffect(() => {
    searchContacts();
  }, [searchContacts]);

  /**
   * Search contacts based on the input value.
   * @param {Event} event
   */
  const handleSearchContact = event => {
    const { value } = event?.target ?? {};
    searchContacts(value).catch(() => {
      // Do something with error.
    });
  };

  /**
   * Set the contact value on the form or trigger the provided onChange callback
   * @param {object|Event} ev
   * @param {object|null} contact
   */
  const setContactValue = (ev, contact) => {
    if (formContext && !onChange) {
      setValue(name, contact);
      return;
    }

    if (onChange) {
      onChange(ev, contact);
    }
  };

  const fetchContacts = async ids => {
    try {
      const promises = ids.map(id => contactsApi.getContact(id));
      const responses = await Promise.allSettled(promises).then(result =>
        result.filter(promise => promise.status === 'fulfilled'),
      );

      return responses.reduce((acc, response) => {
        if (response?.value?.data) {
          return [...acc, response.value.data];
        }
        return acc;
      }, []);
    } catch (err) {
      return [];
    }
  };

  /**
   * Handle the change on the contact search.
   * @param {object|Event} ev
   * @param {any} newValue
   */
  const handleOnChange = async (ev, newValue) => {
    try {
      const shouldValidateContact = validateContact && !enableCreate;

      if (!shouldValidateContact) {
        setContactValue(ev, newValue ?? null);
        return;
      }

      if (newValue) {
        const contacts = await fetchContacts(
          Array.isArray(newValue) ? newValue.map(item => item.id) : [newValue?.id],
        );
        const contactValues = contacts.reduce(
          (acc, contact) => [
            ...acc,
            { ...contact, label: getContactDisplay(contact), value: contact.id },
          ],
          [],
        );

        if (Array.isArray(newValue)) {
          setContactValue(ev, contactValues);
        } else {
          const [firstContact] = contactValues;
          setContactValue(ev, firstContact);
        }
      } else {
        setContactValue(ev, null);
      }
    } catch (err) {
      setContactValue(ev, null);
    }
  };

  const renderContactOption = (option, { inputValue = '' } = {}) => {
    let label = option;

    if (typeof option === 'object') {
      // eslint-disable-next-line prefer-destructuring
      label = option.label;
    }

    return (
      <ListItem component="div" dense disableGutters>
        <ListItemAvatar>
          <Avatar alt={label} src={gravatarUrl(option.email, { letterValue: label })} />
        </ListItemAvatar>
        <ListItemText
          primary={highlightSubstring(label, inputValue)}
          secondary={highlightSubstring(option.email, inputValue)}
        />
        {option?.audience && <Chip label={option.audience} size="small" />}
      </ListItem>
    );
  };

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: option => `${option.label} ${option.email ?? ''}`,
  });

  return (
    <Autocomplete
      {...props}
      name={name}
      onChange={handleOnChange}
      onInputChange={handleSearchContact}
      options={contactSuggestions}
      filterOptions={filterOptions}
      renderOption={renderContactOption}
      autocomplete="off"
      loading={isFetchingContacts}
      enableCreate={enableCreate}
      inputProps={{
        ...(inputProps ?? {}),
        placeholder: intl.formatMessage(translations.fields.contact.placeholder),
      }}
    />
  );
};

ContactSearch.propTypes = {
  ...Autocomplete.propTypes,
  validateContact: PropTypes.bool,
};

ContactSearch.defaultProps = {
  ...Autocomplete.defaultProps,
  validateContact: true,
};

export default ContactSearch;
